"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Images } from "../assets/assets";
import Image from "next/image";
// PERF: GSAP is no longer imported eagerly at module scope (kept out of the
// route's initial JS bundle). It is loaded lazily after mount into gsapRef and
// used only once available. Animation behavior is unchanged.
import type GsapNS from "gsap";

export default function Aboutsection() {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gsapRef = useRef<typeof GsapNS | null>(null);

  // Lazy-load GSAP after hydration so it never blocks first paint / TBT.
  useEffect(() => {
    let mounted = true;
    import("gsap").then((m) => {
      if (mounted) gsapRef.current = m.default;
    });
    return () => {
      mounted = false;
    };
  }, []);

  const imageAssets = [Images?.about, Images?.about_3, Images?.about_2];
  const [order, setOrder] = useState<number[]>([0, 1, 2]);

  // Wrapped shuffle in useCallback so it can be used safely inside useEffect
  const shuffle = useCallback(() => {
    const gsap = gsapRef.current;
    if (!gsap) return; // GSAP not loaded yet — skip this tick (loads within ~1 frame)
    if (!containerRef.current) return;

    const cards = containerRef.current.querySelectorAll(".card-item");
    if (cards.length === 0) return;

    // The visually top card is the last child in our mapped array
    const topCard = cards[cards.length - 1] as HTMLElement;

    const tl = gsap.timeline();

    tl.to(topCard, {
      x: 150,
      rotation: 20,
      opacity: 0.01,
      scale: 0.9,
      duration: 0.5,
      ease: "power2.inOut",
      onComplete: () => {
        setOrder((prev) => {
          const newOrder = [...prev];
          const last = newOrder.pop();
          if (last !== undefined) {
            newOrder.unshift(last);
          }
          return newOrder;
        });
        // Reset position for when it becomes the bottom card
        gsap.set(topCard, { x: 0, rotation: 0, scale: 1, opacity: 1 });
      },
    });
  }, []);

  // AUTO-PLAY LOGIC: Shuffles every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      shuffle();
    }, 3000);

    return () => clearInterval(interval);
  }, [shuffle]);
  return (
    <section className="2xl:pt-[150px] pt-[50px]">
      <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] flex flex-col 2xl:flex-row">
        {/* Left Side: Images */}
        <div className="flex flex-col gap-[20px] w-[max-content] aspect-[1/0.7]">
          <div className="justify-center text-[#00040b] text-base font-[400] uppercase tracking-tight">
            About Us
          </div>
          <div
            ref={containerRef}
            onClick={shuffle}
            className="relative image-container 
                lg:min-w-[650px]
                min-w-[calc(100vw-40px)]
                flex-1
                cursor-pointer
                "
          >
            {order?.map((imgIndex, i) => {
              return (
                <div
                  key={`img-${imgIndex}`}
                  className="card-item absolute w-[55%] aspect-[4/3] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  style={{
                    zIndex: i,
                  }}
                >
                  <Image
                    src={imageAssets[imgIndex]}
                    alt="about us"
                    className="object-cover"
                    fill
                    style={{
                      transform: `rotate(${i === 2 ? 10 : i === 1 ? 16 : 0}deg)`,
                    }}
                    sizes="(max-width: 1024px) 100vw, 33vw"
                    fetchPriority={i === 0 ? "high" : "auto"}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Side: Content */}
        <div className="flex flex-col gap-[30px]">
          <div>
            <div
              className={`text-[#00040b] [font-family:'Clash Display'] font-medium text-[length:var(--about-para)] leading-[var(--about-para-leading)] transition-all duration-300 ${
                !isExpanded ? "line-clamp-4" : ""
              }`}
            >
              <p>
                We work with large scale enterprises as well as growing
                startups, helping them to transform their ideas into{" "}
                <span className="text-[#225BD4] clash-font ">
                  fine-tuned digital solutions
                </span>{" "}
                backed by next-gen technologies including AI/ML, AR/VR, IoT,
                blockchain, and more. At Konstant, we blend technical innovation
                with modern web and app development frameworks and tools to
                ensure each solution we develop stands out in the market.
              </p>
              <p className="mt-4">
                With over 23+ years of industry exposure and a team of 180+ tech
                experts, we carefully understand the{" "}
                <span className="text-[#225BD4]">
                  unique needs and goals of every business{" "}
                </span>
                to craft business-focused solutions, ensuring businesses can
                outpace their competitors in today’s digital landscape. When
                clients come to us with their complex challenges, we transform
                these challenges into AI-driven mobile and web solutions that
                adapt to growing user needs.
              </p>
            </div>

            {/* Toggle Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={isExpanded ? "Read Less" : "Read More"}
              className="inline-flex items-center gap-1 text-[#225BD4] font-semibold cursor-pointer group transition-all duration-200 align-baseline mt-4"
            >
              <span className="hover:underline">
                {isExpanded ? "Read Less" : "Read More"}
              </span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`
                  transition-transform duration-500 ease-in-out
                  ${isExpanded ? "rotate-180" : "rotate-0"}
                `}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:pt-[100px] pt-[20px]">
            <div className="border border-[#ecf0f6] p-[45px] flex flex-col gap-[10px] items-center justify-center text-center">
              <div className="text-[#00040b] clash-font font-semibold leading-none text-[length:var(--about-stat-number)]">
                23+Years
              </div>
              <div className="text-[#636364] font-normal text-[length:var(--about-stat-label)]">
                Industry Experience
              </div>
            </div>
            <div className="border border-[#ecf0f6] p-[45px] flex flex-col gap-[10px] items-center justify-center text-center">
              <div className="text-[#00040b] clash-font font-semibold leading-none text-[length:var(--about-stat-number)]">
                2500+
              </div>
              <div className="text-[#636364] font-normal text-[length:var(--about-stat-label)]">
                Delighted Clients
              </div>
            </div>
            <div className="border border-[#ecf0f6] p-[45px] flex flex-col gap-[10px] items-center justify-center text-center">
              <div className="text-[#00040b] clash-font font-semibold leading-none text-[length:var(--about-stat-number)]">
                180+
              </div>
              <div className="text-[#636364] font-normal text-[length:var(--about-stat-label)]">
                Employees
              </div>
            </div>
            <div className="border border-[#ecf0f6] p-[45px] flex flex-col gap-[10px] items-center justify-center text-center">
              <div className="text-[#00040b] clash-font font-semibold leading-none text-[length:var(--about-stat-number)]">
                64%
              </div>
              <div className="text-[#636364] font-normal text-[length:var(--about-stat-label)]">
                Retention Rate
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
